import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Chip,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { DocumentVersionInfo, DocumentStatus } from '../../types/documents';

interface DocumentVersionHistoryProps {
  open: boolean;
  onClose: () => void;
  versions: DocumentVersionInfo[];
  currentVersion: number;
  documentTitle: string;
  onVersionSelect: (version: number) => void;
}

const getStatusChip = (status: DocumentStatus) => {
  switch (status) {
    case 'DRAFT':
      return <Chip label="Draft" size="small" color="warning" variant="outlined" />;
    case 'FINAL':
      return <Chip label="Final" size="small" color="success" variant="outlined" />;
    case 'ARCHIVED':
      return <Chip label="Archived" size="small" color="default" variant="outlined" />;
  }
};

const DocumentVersionHistory: React.FC<DocumentVersionHistoryProps> = ({
  open,
  onClose,
  versions,
  currentVersion,
  documentTitle,
  onVersionSelect,
}) => {
  // Sort versions in descending order (newest first)
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h6">Version History</Typography>
          <Typography variant="body2" color="text.secondary">
            {documentTitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {sortedVersions.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No version history available</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {sortedVersions.map((version, index) => (
              <ListItem key={version.version} sx={{ p: 0 }}>
                <ListItemButton
                  selected={version.version === currentVersion}
                  onClick={() => onVersionSelect(version.version)}
                  sx={{ py: 2 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" component="span">
                          Version {version.version}
                        </Typography>
                        {version.version === currentVersion && (
                          <Chip label="Current" size="small" color="primary" />
                        )}
                        {index === 0 && version.version !== currentVersion && (
                          <Chip label="Latest" size="small" variant="outlined" />
                        )}
                        {getStatusChip(version.status)}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {new Date(version.createdAt).toLocaleString()}
                        </Typography>
                        {version.revisionPrompt && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              fontStyle: 'italic',
                              maxWidth: '400px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Revision: {version.revisionPrompt}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentVersionHistory;
